import { useMutation } from "@apollo/client"
import { Dialog } from "@radix-ui/react-dialog"
import { Form, Formik, FormikHelpers, FormikProps } from "formik"
import { useState } from "react"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import invariant from "tiny-invariant"
import { gql } from "~/__generated__"
import { imageSearchResultsPath } from "~/common/paths"
import { displayErrors } from "~/common/validations"
import SaveNewSearchFields, {
  SaveNewSearchValues,
  saveNewSearchValidationSchema,
} from "~/components/SaveNewSearchFields"
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/Dialog"
import useImageSearchParamValues from "~/hooks/useImageSearchParamValues"
import Button from "~/ui/Button"

export const IMAGE_SEARCH_CREATE_MUTATION = gql(`
  mutation ImageSearchCreate($input: ImageSearchCreateInput!) {
    imageSearchCreate(input: $input) {
      search {
        id
        name
      }
    }
  }
`)

const NewImageSearchDialog = () => {
  const [mutate] = useMutation(IMAGE_SEARCH_CREATE_MUTATION)
  const [isOpen, setIsOpen] = useState(false)
  const imageSearchParamValues = useImageSearchParamValues()
  const navigate = useNavigate()

  const onSubmit = async (
    values: SaveNewSearchValues,
    { setFieldError }: FormikHelpers<SaveNewSearchValues>
  ) => {
    try {
      const resp = await mutate({
        variables: {
          input: {
            searchInput: {
              name: values.name,
            },
            imageSearchParamsInput: {
              imageCount: Number(imageSearchParamValues.imageCount),
              ganLabelId: imageSearchParamValues.ganLabelId,
            },
          },
        },
      })

      const id = resp?.data?.imageSearchCreate?.search?.id
      invariant(id, "Expected search id")

      toast.success("Search has been saved")
      navigate(imageSearchResultsPath({ id }))
      setIsOpen(false)
    } catch (error: any) {
      console.error(error)
      displayErrors(error?.graphQLErrors, setFieldError)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="outlineSecondary" display="inline" type="button">
          Save Search
        </Button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay className="DialogOverlay" />
        <DialogContent className="DialogContent">
          <DialogTitle className="DialogTitle">Save Search</DialogTitle>
          <div className="mb-4 mt-6">
            <div className="mb-1 text-2xl font-medium text-blue-zodiac">
              Save Search Criteria
            </div>
            <DialogDescription className="DialogDescription">
              Saving your search allows you to quickly view prior query builder
              results.
            </DialogDescription>
          </div>

          <Formik
            initialValues={{ name: "" }}
            validationSchema={saveNewSearchValidationSchema}
            validateOnBlur={false}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }: FormikProps<SaveNewSearchValues>) => (
              <Form className="w-full">
                <SaveNewSearchFields disabled={isSubmitting} />
              </Form>
            )}
          </Formik>
          <DialogClose asChild>
            <button className="IconButton" aria-label="Close" />
          </DialogClose>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default NewImageSearchDialog
